import type { HTMLAttributes } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useHotelPromotion } from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import { useStickyHeaderOffset } from '@/app/components/hotel-detail/hooks/useStickyHeaderOffset.ts';
import { useTranslation } from '../translations/Translation';
import { ScrollToIdButton } from './ScrollToIdLink';
import { pageSectionIds } from './common/consts';
import { useIsMobile } from './hooks/useMediaSSR';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import { useOffersContext } from './OffersProvider';

export function HotelGalleryGoToRoomsButton(
  props: { offset?: number; targetId?: string; onClick: () => void } & HTMLAttributes<HTMLElement>
) {
  const { onClick, offset, targetId, ...rest } = props;
  const { t } = useTranslation();
  return (
    <GoToRoomsButton offset={offset} targetId={targetId} data-test-id="book-cta" onClick={onClick} {...rest}>
      {t('vacation.select.gallery.cta')}
    </GoToRoomsButton>
  );
}

export function GoToRoomsButton(
  props: {
    offset?: number;
    targetId?: string;
    onClick: () => void;
    children: React.ReactNode;
  } & HTMLAttributes<HTMLElement>
) {
  const { onClick, offset, targetId: targetIdOverride, children, ...rest } = props;
  const isMobile = useIsMobile();
  const { commonData } = usePageWrapperData();

  const { showLmnDealsPromotion } = useHotelPromotion();
  const { accommodationOnly } = useUrlSearchParams();

  const { stickyTop } = useStickyHeaderOffset();

  // The per-case clearances were calibrated against the 64px ContentHub band; on the
  // external-header brands the fixed band is header + CSW (MPH-864), so take whichever
  // clears more — stickyTop + 16 matching PriceSticky's breathing room over the same target.
  const defaultOffset = commonData.isApp
    ? 0
    : offset || Math.max(accommodationOnly ? (isMobile ? 72 : 124) : isMobile ? 52 : 76, stickyTop + 16);
  const targetId = targetIdOverride ?? (accommodationOnly ? pageSectionIds.rooms : pageSectionIds.packageDetails);
  const { offersData } = useOffersContext();

  return (
    <ScrollToIdButton
      variant="primary"
      targetId={targetId}
      offset={defaultOffset}
      data-test-id="book-cta"
      onClick={onClick}
      timeout={500}
      isLmnDeal={offersData?.hasFlashSaleOffers && showLmnDealsPromotion}
      {...rest}
    >
      {children}
    </ScrollToIdButton>
  );
}
